import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import { AssignmentOutlined as QuestionnaireIcon } from "@material-ui/icons";
import StackedAlert from "../common/StackedAlert";
import type { ListingListedFor } from "../../../@types/sharedSchema";
import { isQuestionnaireOnlyDocument } from "../../../@types/sharedSchema";
import type { SignatureTemplate } from "../../../@types/signatures";
import { questionnaireLabelContext } from "../../../utils/signatureQuestionnaireName";
import { templateCarriesQuestionnaire } from "../../../utils/signatureQuestionnaireStep";
import { questionnairePropertyAddress, type ListingSendContext } from "./signatureListingContext";

/**
 * The send form's questionnaire pieces (Trello #231, item 8 follow-up; Haim: "in the sending ui and ux
 * it should be clear that the שאלון מוכר will be sent"), kept out of `NewSignatureForm`'s body on
 * purpose: that component is so large that eslint-plugin-react-hooks 4.2 loses count of its code paths,
 * and adding or removing a branch in its body turns dozens of its hooks into false "called
 * conditionally" errors. Decide here; the form only calls.
 */

/**
 * A send that carries the owner questionnaire but whose picked property has no address. It must not
 * go: the questionnaire needs the address, and a send without it used to drop the questionnaire
 * silently while the form said it was attached.
 */
export const isQuestionnaireAddressMissing = (
  sendsQuestionnaire: boolean,
  pickedListingCount: number,
  firstListing: ListingSendContext | undefined
): boolean => sendsQuestionnaire && pickedListingCount > 0 && !questionnairePropertyAddress(firstListing);

/**
 * WHICH contract(s) of a set bring the questionnaire, by the name the client reads (a seller pair: the
 * order, never the exclusivity — Haim looked for it on the exclusivity contract). Null for one
 * contract, and for a set where every contract brings one: "with the contract" says it all.
 */
export const questionnaireCarrierTitlesOf = (setTemplates: SignatureTemplate[]): string | null => {
  if (setTemplates.length < 2) return null;
  const carriers = setTemplates.filter((tp) => templateCarriesQuestionnaire(tp) && !isQuestionnaireOnlyDocument(tp));
  if (carriers.length === 0 || carriers.length === setTemplates.length) return null;
  return carriers.map((tp) => tp.customerFacingName?.trim() || tp.name).join(", ");
};

const SET_VARIANT: Record<string, string> = {
  "signatures.newDoc.sellerQuestionnaireNoteTitle": "signatures.newDoc.sellerQuestionnaireNoteTitleSet",
  "signatures.newDoc.sellerQuestionnaireNoteBody": "signatures.newDoc.sellerQuestionnaireNoteBodySet"
};

/**
 * The notice in the listings card: the questionnaire goes out WITH the contract, and the client fills
 * and signs it right after signing that contract, on the same link; a set names the contract that
 * brings it (`carrierTitles`). The address-prefill sentence stays. A questionnaire on its own keeps its
 * own words (`titleKey` / `bodyKey` are its keys then, and no set wording applies to them).
 */
export function SellerQuestionnaireNotice({
  titleKey,
  bodyKey,
  carrierTitles,
  listedFor,
  addressMissing
}: {
  titleKey: string;
  bodyKey: string;
  /** `questionnaireCarrierTitlesOf`: the set's contract(s) that bring it; null for one contract. */
  carrierTitles: string | null;
  listedFor: ListingListedFor | null | undefined;
  addressMissing: boolean;
}) {
  const { t } = useTranslation();
  const context = questionnaireLabelContext(listedFor);
  const named = !!carrierTitles && !!SET_VARIANT[titleKey];
  const accent = addressMissing ? "error.main" : "info.main";
  return (
    <Box
      data-testid="seller-questionnaire-notice"
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: accent,
        bgcolor: addressMissing ? "rgba(255,72,66,0.06)" : "rgba(24,144,255,0.08)",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25
      }}
    >
      <QuestionnaireIcon sx={{ color: accent, mt: "1px", flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        {/* A questionnaire-only document is not "attached" to anything: it is all that is sent. */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {named
            ? t(SET_VARIANT[titleKey], { title: carrierTitles, context })
            : t(titleKey, { context })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t(named && SET_VARIANT[bodyKey] ? SET_VARIANT[bodyKey] : bodyKey)}
        </Typography>
        {addressMissing && (
          <Typography variant="body2" color="error.main" sx={{ mt: 0.75, fontWeight: 600 }}>
            {t("signatures.newSignatureForm.errors.questionnaireNoAddress", { context })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** The same refusal beside the send button it disables: there is no "send anyway" for it. */
export function QuestionnaireAddressAlert({ show, listedFor }: { show: boolean; listedFor: ListingListedFor | null | undefined }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <StackedAlert severity="error" data-testid="questionnaire-address-missing">
      <Typography variant="body2">
        {t("signatures.newSignatureForm.errors.questionnaireNoAddress", { context: questionnaireLabelContext(listedFor) })}
      </Typography>
    </StackedAlert>
  );
}
